(() => {
  'use strict';

  const root = document.querySelector('.cart-clone-root');
  if (!root) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const LANDING_URL = '/pages/oferta';

  const $ = (sel, scope = root) => scope.querySelector(sel);
  const $$ = (sel, scope = root) => [...scope.querySelectorAll(sel)];

  const moneyFormat = (cents) => {
    const value = (cents / 100);
    return value.toLocaleString(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR'
    });
  };

  // === Toast ===
  let toast = document.querySelector('.cart-clone-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'cart-clone-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  let toastTimer = 0;
  const showToast = (message) => {
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const live = $('[data-live-region]');
  const announce = (msg) => {
    if (live) live.textContent = msg;
    showToast(msg);
  };

  // === Cart count animation ===
  const cartCount = $('[data-cart-count]');
  const bumpCount = () => {
    if (!cartCount) return;
    cartCount.classList.remove('bump');
    void cartCount.offsetWidth;
    cartCount.classList.add('bump');
  };

  // === Sticky header shadow ===
  const header = $('[data-sticky-header]');
  if (header) {
    const setShadow = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    setShadow();
    window.addEventListener('scroll', setShadow, { passive: true });
  }

  // === Overlay (drawer + modal) ===
  const setOpen = (overlay, isOpen) => {
    overlay.classList.toggle('is-open', isOpen);
    overlay.setAttribute('aria-hidden', String(!isOpen));
    const anyOpen = isOpen || root.querySelector('.search-modal.is-open') || root.querySelector('.nav-drawer.is-open');
    document.body.classList.toggle('cart-clone-locked', !!anyOpen);
  };
  const bindOverlay = ({ rootSel, openSel, closeSel, focusSel }) => {
    const overlay = $(rootSel);
    const openBtn = $(openSel);
    if (!overlay || !openBtn) return;
    const closeBtn = overlay.querySelector(closeSel);
    const open = () => {
      setOpen(overlay, true);
      openBtn.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => overlay.querySelector(focusSel)?.focus(), 40);
    };
    const close = () => {
      setOpen(overlay, false);
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.focus();
    };
    openBtn.addEventListener('click', open);
    closeBtn?.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });
  };
  bindOverlay({ rootSel: '#nav-drawer', openSel: '.nav-toggle', closeSel: '.drawer-close', focusSel: 'a' });
  bindOverlay({ rootSel: '#search-modal', openSel: '.search-toggle', closeSel: '.modal-close', focusSel: 'input' });

  // === Shopify cart actions ===
  let inFlight = false;
  const callCart = async (path, payload) => {
    if (inFlight) return null;
    inFlight = true;
    try {
      const resp = await fetch(`/cart/${path}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data && data.description || 'Cart error');
      return data;
    } catch (err) {
      announce(err.message || 'Cart update failed');
      return null;
    } finally {
      inFlight = false;
    }
  };

  const refreshCartSummary = async () => {
    try {
      const resp = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await resp.json();
      if (cartCount) {
        cartCount.textContent = String(cart.item_count);
        bumpCount();
      }
      const subtotalEl = root.querySelector('[data-subtotal]');
      if (subtotalEl) subtotalEl.textContent = moneyFormat(cart.total_price);
      return cart;
    } catch { return null; }
  };

  const changeLine = async (line, quantity) => {
    const data = await callCart('change', { line, quantity });
    if (!data) return;
    if (cartCount) cartCount.textContent = String(data.item_count);
    bumpCount();
    const subtotalEl = root.querySelector('[data-subtotal]');
    if (subtotalEl) subtotalEl.textContent = moneyFormat(data.total_price);
    const itemRow = root.querySelector(`[data-cart-item][data-line="${line}"]`);
    if (itemRow) {
      const updatedItem = data.items[line - 1];
      if (!updatedItem) {
        itemRow.classList.add('is-removing');
        window.setTimeout(() => itemRow.remove(), reduceMotion ? 0 : 280);
        if (data.item_count === 0) {
          root.querySelector('[data-cart-table]')?.setAttribute('hidden', '');
          root.querySelector('[data-summary]')?.setAttribute('hidden', '');
          root.querySelector('[data-upsell]')?.setAttribute('hidden', '');
          const empty = root.querySelector('[data-empty-cart]');
          if (empty) empty.hidden = false;
        }
        return;
      }
      const qty = itemRow.querySelector('[data-quantity]');
      if (qty) qty.value = String(updatedItem.quantity);
      const lineTotal = itemRow.querySelector('[data-line-total]');
      if (lineTotal) lineTotal.textContent = moneyFormat(updatedItem.final_line_price);
      const lineCompare = itemRow.querySelector('[data-line-compare]');
      if (lineCompare && updatedItem.original_line_price > updatedItem.final_line_price) {
        lineCompare.textContent = moneyFormat(updatedItem.original_line_price);
      }
      itemRow.querySelector('[data-decrease]')?.toggleAttribute('disabled', updatedItem.quantity <= 1);
    }
  };

  // === Cart item event delegation ===
  root.addEventListener('click', (e) => {
    const incBtn = e.target.closest('[data-increase]');
    const decBtn = e.target.closest('[data-decrease]');
    const remBtn = e.target.closest('[data-remove]');
    const itemRow = e.target.closest('[data-cart-item]');
    const line = itemRow ? parseInt(itemRow.dataset.line, 10) : 0;

    if (incBtn && line) {
      e.preventDefault();
      const qty = itemRow.querySelector('[data-quantity]');
      const newQty = (parseInt(qty.value, 10) || 1) + 1;
      changeLine(line, newQty);
      return;
    }
    if (decBtn && line) {
      e.preventDefault();
      const qty = itemRow.querySelector('[data-quantity]');
      const newQty = Math.max(1, (parseInt(qty.value, 10) || 1) - 1);
      changeLine(line, newQty);
      return;
    }
    if (remBtn && line) {
      e.preventDefault();
      itemRow.classList.add('is-removing');
      window.setTimeout(() => changeLine(line, 0), reduceMotion ? 0 : 200);
      return;
    }

    const updateBtn = e.target.closest('[data-update]');
    if (updateBtn) {
      e.preventDefault();
      refreshCartSummary().then(() => announce('Cart actualizado.'));
      return;
    }

    const upgradeBtn = e.target.closest('[data-upgrade]');
    if (upgradeBtn) {
      e.preventDefault();
      const variantId = upgradeBtn.dataset.variantId;
      if (variantId) {
        callCart('add', { id: variantId, quantity: 1 }).then((r) => {
          if (r) {
            refreshCartSummary();
            announce('Upgrade añadido al carrito.');
            upgradeBtn.textContent = 'Añadido';
          }
        });
      } else {
        showToast('Configura el variant_id del upsell en el bloque correspondiente.');
      }
      return;
    }

    const upgradeCardBtn = e.target.closest('[data-upgrade-card]');
    if (upgradeCardBtn) {
      e.preventDefault();
      showToast('Vista previa del upgrade.');
      return;
    }

    const ctaLink = e.target.closest('a.primary-button');
    if (ctaLink) return; // dejar comportamiento normal (link a landing)
  });

  // qty input direct edit
  root.addEventListener('change', (e) => {
    const input = e.target.closest('[data-quantity]');
    if (!input) return;
    const itemRow = input.closest('[data-cart-item]');
    if (!itemRow) return;
    const line = parseInt(itemRow.dataset.line, 10);
    const newQty = Math.max(1, parseInt(input.value, 10) || 1);
    changeLine(line, newQty);
  });

  // Cart form submit → checkout
  const cartForm = root.querySelector('[data-cart-form]');
  if (cartForm) {
    cartForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const submitter = e.submitter;
      if (submitter && submitter.name === 'checkout') {
        window.location.href = '/checkout';
      }
    });
  }

  // === Search via Shopify predictive ===
  const renderSearch = (results) => {
    const box = root.querySelector('[data-search-results]');
    if (!box) return;
    if (!results || results.length === 0) {
      box.innerHTML = `<p>Sin resultados.</p>`;
      return;
    }
    box.innerHTML = results.map((p) => `
      <a class="search-card" href="${p.url}">
        <img src="${p.image}" alt="${p.title}">
        <strong>${p.title}</strong>
        <span>${p.price}</span>
      </a>
    `).join('');
  };

  let searchTimer = 0;
  const runSearch = (query) => {
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(async () => {
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`;
        const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
        const data = await resp.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        renderSearch(products.map((p) => ({
          title: p.title,
          url: p.url,
          image: p.featured_image && p.featured_image.url ? p.featured_image.url : p.image,
          price: p.price || ''
        })));
      } catch (err) {
        renderSearch([]);
      }
    }, 220);
  };

  $$('[data-search-form]').forEach((form) => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = form.querySelector('input');
      if (input && input.value) runSearch(input.value);
    });
  });
  const searchInput = root.querySelector('#search-query');
  if (searchInput) {
    searchInput.addEventListener('input', (e) => runSearch(e.currentTarget.value));
  }

  // === Coupon code copy-to-clipboard ===
  $$('[data-copy-code]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const code = btn.dataset.copyCode;
      if (!code) return;
      let ok = false;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(code);
          ok = true;
        }
      } catch (_) { /* fall through */ }
      if (!ok) {
        // Fallback for browsers without clipboard API / http
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); ok = true; } catch (_) {}
        document.body.removeChild(ta);
      }
      if (!ok) { showToast('No se pudo copiar. Selecciónalo manualmente.'); return; }
      btn.classList.add('is-copied');
      const innerWrap = btn.querySelector('.pack3-editorial__copy-inner');
      const target = innerWrap || btn;
      const originalHtml = btn.dataset.originalLabel || target.innerHTML;
      btn.dataset.originalLabel = originalHtml;
      target.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true" style="margin-right:10px;vertical-align:-2px"><path d="M5 12l5 5L20 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>Copiado';
      showToast(`Código ${code} copiado. Aplícalo en el checkout.`);
      window.setTimeout(() => {
        btn.classList.remove('is-copied');
        target.innerHTML = originalHtml;
      }, 2200);
    });
  });

  // === Countdown — 10 min por sesion, plazo compartido en window.RU_OFFER ===
  // (se define inline en layout/theme.liquid). Al vencer se queda en 00:00: la
  // seccion NO se oculta, para no perder el aviso del descuento en el carrito.
  const countdown = $('[data-countdown]');
  if (countdown) {
    const parts = {
      days: countdown.querySelector('[data-days]'),
      hours: countdown.querySelector('[data-hours]'),
      minutes: countdown.querySelector('[data-minutes]'),
      seconds: countdown.querySelector('[data-seconds]')
    };
    const pad = (n) => (n < 10 ? '0' + n : '' + n);
    const tick = () => {
      const p = window.RU_OFFER?.parts?.() ?? { d: 0, h: 0, m: 0, s: 0 };
      if (parts.days) parts.days.textContent = pad(p.d);
      if (parts.hours) parts.hours.textContent = pad(p.h);
      if (parts.minutes) parts.minutes.textContent = pad(p.m);
      if (parts.seconds) parts.seconds.textContent = pad(p.s);
    };
    tick();
    window.setInterval(tick, 1000);
  }

  // === Discount-code tooltip ===
  const ryeBtn = $('[data-rye]');
  const ryeTip = $('[data-rye-tooltip]');
  if (ryeBtn && ryeTip) {
    ryeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      ryeTip.hidden = !ryeTip.hidden;
    });
    document.addEventListener('click', (e) => {
      if (ryeTip.hidden) return;
      if (e.target === ryeBtn || ryeTip.contains(e.target)) return;
      ryeTip.hidden = true;
    });
  }

  // === Reveal on scroll ===
  root.classList.add('reveal-ready');
  const targets = ['.cart-heading', '.cart-table', '.cart-upsell', '.cart-summary']
    .map((s) => root.querySelector(s)).filter(Boolean);
  if (reduceMotion || !('IntersectionObserver' in window)) {
    targets.forEach((t) => t.classList.add('is-visible'));
  } else {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    targets.forEach((t) => io.observe(t));
  }
})();

/* ------------------------------------------------------------------
   Limpieza de la promo de regalo retirada (2026-09-03).
   El descuento que ponia la bolsa suelta a 0 esta desactivado, asi que
   una linea de "Regalo" que sobreviva en un carrito viejo se cobraria a
   precio completo. Si aparece, se quita. Nunca se anade nada.
   Cuando ya no queden carritos con esa linea, este bloque se puede borrar.
   ------------------------------------------------------------------ */
(() => {
  'use strict';
  const cartRoot = document.querySelector('.cart-clone-root');
  if (!cartRoot) return;

  const GIFT_PROP = 'Regalo';
  const GUARD = 'ryzeGiftGuard';

  const isGift = (item) => !!(item.properties && item.properties[GIFT_PROP]);

  /* Recargar tras la limpieza, pero nunca dos veces seguidas: si el
     cambio no cuaja, el guard corta el bucle. */
  const justCleaned = Date.now() - Number(sessionStorage.getItem(GUARD) || 0) < 10000;
  if (justCleaned) return;

  fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((resp) => resp.json())
    .then((cart) => {
      const gift = (cart.items || []).filter(isGift)[0];
      if (!gift) return null;

      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: gift.key, quantity: 0 }),
      }).then((resp) => {
        if (!resp.ok) return null;
        sessionStorage.setItem(GUARD, String(Date.now()));
        window.location.reload();
        return null;
      });
    })
    .catch(() => {});
})();
